// ============================================================================
// Captive Portal (FCP) report wizard service — bare minimum.
//
// Backs the "Captive Portal" report type in the generic Add Report wizard.
// All calls go to AppCom's /captive-portal/v2/*, which forwards report
// generation to insight-job-service (insight-cloud repo).
//
//   fetchCaptivePortalSsidGroups()        Filter & Scope tree
//   submitCaptivePortalReport(payload)    submit  -> { jobId }
//   fetchCaptivePortalReportStatus(jobId) job status
//   fetchCaptivePortalReportDownloadUrl   presigned download URL
//   awaitCaptivePortalReportDownloadUrl   poll status -> download URL (for the wizard)
// ============================================================================
import useApi from '../hooks/useApi';

// useApi's 4th arg is the OPTIONAL-header whitelist (accountId + apiKey are
// always sent by default). AppCom's /captive-portal/v2/* endpoints require the
// internal user id (X- 'userId' header = the Mongo _Id) for auth — without it
// they reject with "Invalid Permission". So opt the userId header in here.
const HEADERS = ['userId'];

/** SSID-groups tree for the Filter & Scope step (scoped to the authenticated org). */
export const fetchCaptivePortalSsidGroups = async () =>
  useApi('CAPTIVE_PORTAL_SSID_GROUPS', {}, {}, HEADERS);

/** Submit a captive-portal report. AppCom forwards to insight-job-service. */
export const submitCaptivePortalReport = async (payload) =>
  useApi('CAPTIVE_PORTAL_SUBMIT_REPORT', {}, { data: payload }, HEADERS);

/** Poll a report job's status. */
export const fetchCaptivePortalReportStatus = async (jobId) =>
  useApi('CAPTIVE_PORTAL_REPORT_STATUS', { jobId }, {}, HEADERS);

/** Fresh presigned download URL for a completed report. */
export const fetchCaptivePortalReportDownloadUrl = async (jobId) =>
  useApi('CAPTIVE_PORTAL_REPORT_DOWNLOAD', { jobId }, {}, HEADERS);

/**
 * Cancel (deactivate) a recurring report schedule by id. AppCom forwards to
 * insight-job-service's DELETE /job-schedules/{scheduleId}. Used when a
 * scheduled report is edited — submit always creates a NEW schedule, so the
 * previous cron must be cancelled to avoid duplicate recurring runs.
 */
export const cancelCaptivePortalSchedule = async (scheduleId) =>
  useApi('CAPTIVE_PORTAL_CANCEL_SCHEDULE', { scheduleId }, {}, HEADERS);

// AppCom may wrap the id under response, data, OR details depending on endpoint.
const pickJobId = (res) =>
  res?.jobId || res?.response?.jobId || res?.data?.jobId || res?.details?.jobId;

// Pick the job-state string. AppCom wrappers carry a success flag in
// response.status that may be boolean true or string 'true'/'false'; either
// form must be skipped so the real job state (SUCCEEDED/FAILED/RUNNING etc.)
// from details.status or data.status is returned instead.
// Candidates are ordered: job-specific wrappers first, generic last.
const WRAPPER_BOOLEANS = new Set(['true', 'false']);
const pickStatus = (res) => {
  const candidates = [
    res?.details?.status,
    res?.data?.status,
    res?.status,
    res?.response?.status
  ];
  for (const v of candidates) {
    if (v == null) continue;
    if (typeof v === 'boolean') continue;              // boolean true/false
    const s = String(v).toUpperCase();
    if (s.length === 0) continue;
    if (WRAPPER_BOOLEANS.has(s.toLowerCase())) continue; // string 'true'/'false'
    return s;
  }
  return '';
};

// Presigned download URL — check all wrapper shapes AppCom may use,
// including the details wrapper (same pattern as pickJobId/pickStatus).
const pickUrl = (res) =>
  res?.url || res?.downloadUrl ||
  res?.response?.url || res?.response?.downloadUrl ||
  res?.data?.url || res?.data?.downloadUrl ||
  res?.details?.url || res?.details?.downloadUrl || '';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ---------------------------------------------------------------------------
// Status classification — the job-service / AppCom may report any of several
// terminal spellings. Normalize to three buckets the list UI renders against.
// ---------------------------------------------------------------------------
const CP_STATUS_SUCCESS = ['SUCCEEDED', 'SUCCESS', 'COMPLETED'];
const CP_STATUS_FAILED = ['FAILED', 'CANCELLED', 'CANCELED', 'ERROR'];

export const CP_REPORT_STATE = Object.freeze({
  COMPLETED: 'completed',
  FAILED: 'failed',
  IN_PROGRESS: 'in_progress'
});

/** Map a raw status string to one of CP_REPORT_STATE. Unknown/empty => in_progress. */
export const classifyCaptivePortalStatus = (status) => {
  const s = (status || '').toString().toUpperCase();
  if (CP_STATUS_SUCCESS.includes(s)) return CP_REPORT_STATE.COMPLETED;
  if (CP_STATUS_FAILED.includes(s)) return CP_REPORT_STATE.FAILED;
  return CP_REPORT_STATE.IN_PROGRESS;
};

/** Fetch the current job state for a CP report, normalized to CP_REPORT_STATE. */
export const getCaptivePortalReportState = async (jobId) =>
  classifyCaptivePortalStatus(pickStatus(await fetchCaptivePortalReportStatus(jobId)));

/**
 * Fetch a FRESH presigned download URL for a completed report. Re-minted on
 * every call by the job-service (presigned URLs expire after ~30 min), so the
 * list always opens a currently-valid link. Returns the URL string or ''.
 */
export const getCaptivePortalDownloadUrl = async (jobId) =>
  pickUrl(await fetchCaptivePortalReportDownloadUrl(jobId)) || '';

/**
 * Poll a freshly-submitted job until it is terminal, then return its presigned
 * download URL. Used by the wizard so the user can download right after submit.
 * Returns the URL string, or null if it didn't complete within the budget.
 */
export const awaitCaptivePortalReportDownloadUrl = async (jobId, { attempts = 15, intervalMs = 3000 } = {}) => {
  for (let i = 0; i < attempts; i++) {
    const st = pickStatus(await fetchCaptivePortalReportStatus(jobId));
    if (st === 'SUCCEEDED' || st === 'SUCCESS' || st === 'COMPLETED') {
      return pickUrl(await fetchCaptivePortalReportDownloadUrl(jobId));
    }
    if (st === 'FAILED' || st === 'CANCELLED' || st === 'CANCELED' || st === 'ERROR') {
      return null;
    }
    await sleep(intervalMs);
  }
  return null;
};

export { pickJobId };

/**
 * Build the AppCom submit body from the wizard form + selected SSID rows.
 * Minimal: what insight-job-service needs to render the report.
 *
 * @param {object} form          wizard form state
 * @param {Array}  selectedSsids Filter & Scope rows: { scope, ssidName, captivePortalId, wirelessOrgId|wirelessNetworkId, networkId }
 */
export const buildCaptivePortalReportPayload = (form = {}, selectedSsids = []) => {
  const base = {
    reportName: (form.ReportName || form.reportName || '').trim(),
    // The wizard stores the description in `reportDes` (same field the save
    // payload uses); fall back to Description/description for any other caller.
    description: form.reportDes || form.Description || form.description || '',
    format: (form.format || form.reportFormat || 'PDF').toUpperCase(),
    // Branding logo choice ('1' = VAR/MSP logo, '2' = Organization logo). AppCom
    // resolves this to a logo URL for the report cover.
    brandingLogo: form.brandingLogo || '2',
    scope: (selectedSsids || []).map((s) => ({
      scope: s.scope,
      ssidName: s.ssidName,
      captivePortalId: s.captivePortalId,
      wirelessOrgId: s.wirelessOrgId,
      wirelessNetworkId: s.wirelessNetworkId,
      networkId: s.networkId
    }))
  };
  // PRJCBUGEN-66028: the guest form fields picked in the wizard. Omitted when never touched, which
  // the job service treats as "every configured field".
  if (Array.isArray(form.cpFormFields)) {
    base.formFields = form.cpFormFields;
  }
  const period = form.cpPeriod || '7';
  if (period === 'custom') {
    // Fixed date range -> one-shot only (AppCom rejects scheduling with explicit dates).
    return { ...base, startDate: form.cpStartDate, endDate: form.cpEndDate };
  }
  // Rolling preset -> lookbackDays; schedulable (repeatType '2' = Scheduled).
  return {
    ...base,
    lookbackDays: Number(period),
    repeatType: form.repeatType || '1',
    frequency: form.frequency || '2'
  };
};

// Fields that always have their own report column (Email / Full Name / Phone / City / Country),
// so they are not offered in the form-field picker. Mirrors the job service's mapping: an email-
// or phone-typed field, or one whose label is a known identity name.
const IDENTITY_FIELD_KEYS = new Set([
  'fullname', 'name', 'firstname', 'lastname', 'email', 'emailaddress', 'phone', 'mobile', 'phoneno',
  'phonenumber', 'mobileno', 'mobilenumber', 'city', 'country'
]);
const normalizeFieldKey = (s) => String(s || '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

/** True when a configured field {label, data} lands in a fixed report column, not a custom one. */
export const isIdentityFormField = (field = {}) => {
  const data = String(field.data || '').trim().toLowerCase();
  return data === 'email' || data === 'phone' || IDENTITY_FIELD_KEYS.has(normalizeFieldKey(field.label));
};

/**
 * PRJCBUGEN-66028: the custom guest form fields offered in the report's field picker — the union of
 * the selected SSIDs' configured fields (row.formFields from /ssid-groups), first-seen order,
 * one entry per label (case-insensitive), identity fields excluded.
 */
export const availableCpFormFields = (selectedSsids = []) => {
  const seen = new Set();
  const out = [];
  (selectedSsids || []).forEach((row) => {
    (row?.formFields || []).forEach((f) => {
      const label = String(f?.label || '').trim();
      const key = label.toLowerCase();
      if (!label || seen.has(key) || isIdentityFormField(f)) return;
      seen.add(key);
      out.push(label);
    });
  });
  return out;
};

/**
 * The picker's layout for several selected SSIDs: fields configured on two or more of them
 * ("common", each with the SSIDs that have it), then the fields unique to one SSID, grouped
 * under that SSID. Every label appears exactly once, so the selection stays one list of labels.
 * Same label rules as availableCpFormFields (case-insensitive, identity fields excluded).
 *
 * @returns {{ common: Array<{label: string, ssidNames: string[]}>,
 *             bySsid: Array<{key: string, ssidName: string, labels: string[]}>, ssidCount: number }}
 */
export const groupCpFormFields = (selectedSsids = []) => {
  const rows = (selectedSsids || []).filter(Boolean);
  const nameCounts = rows.reduce((m, r) => m.set(r.ssidName, (m.get(r.ssidName) || 0) + 1), new Map());
  // Two location SSIDs can share a name; tell their groups apart by captive portal id.
  const displayName = (r) =>
    nameCounts.get(r.ssidName) > 1 && r.captivePortalId ? `${r.ssidName} (${r.captivePortalId})` : r.ssidName || '';
  const byLabel = new Map(); // lower-cased label -> { label, rowIndexes: Set }
  rows.forEach((row, i) => {
    availableCpFormFields([row]).forEach((label) => {
      const key = label.toLowerCase();
      if (!byLabel.has(key)) byLabel.set(key, { label, rowIndexes: new Set() });
      byLabel.get(key).rowIndexes.add(i);
    });
  });
  const common = [];
  const specific = new Map(); // row index -> labels
  byLabel.forEach(({ label, rowIndexes }) => {
    if (rowIndexes.size > 1) {
      common.push({ label, ssidNames: [...rowIndexes].map((i) => displayName(rows[i])) });
    } else {
      const i = [...rowIndexes][0];
      specific.set(i, [...(specific.get(i) || []), label]);
    }
  });
  const bySsid = rows
    .map((row, i) => ({ key: `${row.captivePortalId || ''}|${row.ssidName || ''}|${i}`, ssidName: displayName(row), labels: specific.get(i) || [] }))
    .filter((g) => g.labels.length > 0);
  return { common, bySsid, ssidCount: rows.length };
};

/** True when the form represents a scheduled (recurring) CP report. */
export const isScheduledCaptivePortal = (form = {}) =>
  (form.cpPeriod || '7') !== 'custom' && (form.repeatType || '1') === '2';

const pickScheduleId = (res) =>
  res?.scheduleId || res?.response?.scheduleId || res?.data?.scheduleId || res?.details?.scheduleId;

export { pickScheduleId };
